import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';
import { useEcho } from '@laravel/echo-react';
import { useDuplicateStore } from '@/hooks/data/assets/useDuplicateStore';
import { QueryKey } from '@/lib/queryKeys';

const DuplicateCompletedListener = ({ assetId }: { assetId: string }) => {
  const queryClient = useQueryClient();
  const removePendingDuplicate = useDuplicateStore((state) => state.removePendingDuplicate);

  const channelName = useMemo(() => `asset.${assetId}`, [assetId]);

  const handleDuplicated = useCallback(
    (event: { id?: string }) => {
      if (event?.id !== assetId) {
        return;
      }

      void queryClient.invalidateQueries({ queryKey: [QueryKey.Asset, assetId] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.AssetData] });

      removePendingDuplicate(assetId);
    },
    [assetId, queryClient, removePendingDuplicate],
  );

  useEcho(channelName, '.asset.duplicated', handleDuplicated, [channelName, handleDuplicated]);

  return null;
};

/**
 * Mounted above the page tree so it survives navigation away from wherever
 * "Duplicate" was clicked. Subscribes one channel per pending duplicate id.
 */
export const DuplicateProgressManager = () => {
  const pendingAssetIds = useDuplicateStore(useShallow((state) => state.pendingAssetIds));

  return (
    <>
      {pendingAssetIds.map((id) => (
        <DuplicateCompletedListener key={id} assetId={id} />
      ))}
    </>
  );
};
